import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useForgotPassword } from "@/lib/api";
import { logger } from "@/lib/logger";

const PROGRAM_NAME = "Innovation Leadership Program";

export function ForgotPasswordPage() {
  const forgotMutation = useForgotPassword();
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    logger.debug("[forgot-password] requesting reset for", email);
    try {
      await forgotMutation.mutateAsync({ email });
    } catch (err) {
      // The endpoint is designed never to fail for the caller; log and still
      // show the confirmation so we never reveal whether the account exists.
      logger.debug("[forgot-password] request error (ignored):", err);
    }
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-4">
        <Card className="w-full max-w-md border-0 shadow-2xl">
          <CardHeader className="text-center">
            <div className="bg-accent/10 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
              <Mail className="text-accent h-8 w-8" />
            </div>
            <CardTitle className="font-heading text-primary text-2xl font-bold">
              Check your email
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-muted mb-6">
              If an account exists for <span className="text-foreground font-medium">{email}</span>,
              we&apos;ve sent a link to reset your password. The link expires after a short while.
            </p>
            <p className="text-muted mb-6 text-sm">
              Didn&apos;t receive it? Check your spam folder, or try again in a few minutes.
            </p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">Back to sign in</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="bg-background flex min-h-screen items-center justify-center px-4">
      <Card className="w-full max-w-md border-0 shadow-2xl">
        <CardHeader className="space-y-3 px-8 pt-8 text-center md:px-10 md:pt-10">
          <p className="text-primary text-sm font-semibold tracking-[0.24em] uppercase">
            {PROGRAM_NAME}
          </p>
          <CardTitle className="font-heading text-foreground text-2xl font-semibold">
            Reset your password
          </CardTitle>
          <p className="text-muted text-sm">
            Enter the email you use to sign in and we&apos;ll send you a reset link.
          </p>
        </CardHeader>
        <CardContent className="px-8 pb-8 md:px-10">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email" className="text-foreground text-sm font-medium">
                Email
              </label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              disabled={forgotMutation.isPending}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
            >
              {forgotMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send reset link"
              )}
            </Button>
            <p className="text-muted text-center text-sm">
              Remembered it?{" "}
              <Link to="/login" className="text-accent hover:underline">
                Back to sign in
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
